/**
 * Profile / preferences screen.
 *
 * Surfaces the per-user settings that affect how alerts are emitted
 * and consumed:
 * - Whether a low battery (under 20%) pops up on this computer; very low
 *   always does (Akash, 2026-10-31).
 * - Fire alarm audio mode (always / mute-with-rest / never).
 * - Bell dropdown size + auto-archive window.
 * - Cleaned-zone colouring: which Overview views colour the zones, and
 *   how many days until a cleaned zone is fully grey.
 * - Email notifications (server-side, unlike everything above).
 *
 * Also exposes CSV export of the active site's alerts.
 *
 * TWO THINGS HERE ARE DELIBERATELY BLUNT, because both were promises
 * the system could not keep:
 *
 * - The email toggle names every reason mail would not reach you (no
 *   provider on the server, no address, no site assignment). Ticking a
 *   box that says "the server sends these" and then never being paged
 *   is the failure mode this screen used to produce silently.
 * - The CSV export fetches open AND closed alerts from the server.
 *   It used to serialise whatever the bell's cache happened to hold —
 *   open alerts only — under a label offering compliance audits and
 *   post-incident reviews, which are precisely the uses that need the
 *   closed ones.
 */
import { useCallback, useMemo, useState } from 'react';
import { AlertBanner } from '../../design-system/components/AlertBanner';
import { RoleGuard } from '../../auth/RoleGuard';
import { ALERTS_ROLES, hasAnyRole } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { useUserPreferences } from '../../state/useUserPreferences';
import {
  BATTERY_LOW_AFTER_MIN,
  BATTERY_LOW_PCT,
  BATTERY_VERY_LOW_PCT,
} from '../../state/battery-levels';
import { useActiveSite } from '../../state/useActiveSite';
import { adaptAlert, type AlertViewModel } from '../../api/adapters';
import { formatActor } from '../Audit/format-actor';
import { getRuntimeConfig } from '../../config/runtime-config';
import { getRequestBearerToken } from '../../auth/request-auth';
import {
  useEmailDeliverability,
  useNotificationPrefs,
  useUpdateNotificationPrefs,
} from '../../api/hooks/useNotificationPrefs';
import type { AlertSeverity } from '../../api/v1';
import './ProfileScreen.css';
import { HelpNote, HelpScreenNote } from '../../help';
import {
  CLEANED_DAYS_MAX,
  CLEANED_DAYS_MIN,
  clampCleanedDays,
} from '../Twin/cleaned-zones';

type FireAlarmMode = 'always' | 'mute-with-rest' | 'never';

/** The threshold's choices: every whole day the setting allows. */
const CLEANED_DAY_CHOICES = Array.from(
  { length: CLEANED_DAYS_MAX - CLEANED_DAYS_MIN + 1 },
  (_, i) => CLEANED_DAYS_MIN + i,
);

/** `api/alerts.py::list_alerts` refuses a larger `limit`. */
const EXPORT_LIMIT = 500;

async function fetchAlertsForExport(siteId: string): Promise<unknown[]> {
  const token = getRequestBearerToken();
  const url =
    `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(siteId)}` +
    `/alerts?state=all&limit=${EXPORT_LIMIT}`;
  const res = await fetch(url, {
    cache: 'no-store',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as { alerts?: unknown[] };
  return body.alerts ?? [];
}

function downloadBlob(filename: string, content: string, mime: string): void {
  if (typeof window === 'undefined') return;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Defer revoke so the download has time to start in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function csvEscape(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * ISO-8601 UTC from whichever shape the adapter produced — epoch
 * milliseconds for a backend row, an ISO string for the mock
 * publisher's. The raw value is exported alongside it: a reader needs
 * a date they can read, and a script needs one it can sort.
 */
function isoTimestamp(value: string): string {
  if (!value.trim()) return '';
  const asMs = Number(value);
  const d = Number.isFinite(asMs) ? new Date(asMs) : new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/**
 * Who acked or resolved the row, using the Audit screen's precedence so
 * the export and the Acked column can never disagree: the email
 * snapshotted at write time, else the raw subject.
 *
 * EMPTY MEANS NO SUBJECT WAS RECORDED, and that is a smaller claim than
 * "no person decided". Three things produce it: the alerts engine's own
 * mute sweep, the evaluator's auto-close, and a row nothing has acted on
 * yet. A FIRE alert ended by a named supervisor used to land here too --
 * `FireModeService._close_alert` closed the row without stamping a
 * resolver -- which read to an incident review as an automatic close of
 * the highest-stakes row the site produces. It stamps the actor now, so
 * the blank means what this comment says it means.
 *
 * A swept row still carries a resolved TIMESTAMP with no name, which is
 * the honest shape: something closed it, and nobody decided to.
 */
function actorLabel(sub: string | undefined, email: string | undefined): string {
  if (!sub) return '';
  return formatActor({ actor_sub: sub, actor_email: email ?? null });
}

function isoFromMs(ms: number | undefined): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** Convert an alert view-model row to a CSV-formatted line. */
function alertToCsvRow(a: AlertViewModel): string {
  const fields = [
    a.id,
    isoTimestamp(a.timestamp),
    a.timestamp,
    a.severity,
    a.category ?? '',
    a.source,
    a.subtitle ?? '',
    a.message,
    a.status,
    a.robotName ?? '',
    a.robotBattery ?? '',
    a.taskId ?? '',
    a.navigateTo ?? '',
    a.deepLink ?? '',
    // Ownership, appended at the END so a script reading this file by
    // column index keeps working. The export is the console's only view
    // of CLOSED alerts, so it is the only place the resolver is ever
    // visible -- the Alerts tab is open-only. An engine-closed row names
    // nobody in the actor columns, which is the truth about it, and
    // still carries the time it was closed. See `actorLabel`.
    actorLabel(a.ackedBySub, a.ackedByEmail),
    isoFromMs(a.ackedAtMs),
    actorLabel(a.resolvedBySub, a.resolvedByEmail),
    isoFromMs(a.resolvedAtMs),
  ];
  return fields.map(csvEscape).join(',');
}

const CSV_HEADER = [
  'id',
  'timestamp_iso',
  'timestamp_raw',
  'severity',
  'category',
  'source',
  'subtitle',
  'message',
  'status',
  'robot_name',
  'robot_battery',
  'task_id',
  'navigate_to',
  'deep_link',
  'acked_by',
  'acked_at_iso',
  'resolved_by',
  'resolved_at_iso',
].join(',');

export function ProfileScreen() {
  const { user, roles } = useAuth();
  const canSeeAlerts = hasAnyRole(roles, ALERTS_ROLES);
  const { prefs, update, reset } = useUserPreferences();
  const { site, sites } = useActiveSite();
  const emailPrefs = useNotificationPrefs();
  const updateEmailPrefs = useUpdateNotificationPrefs();
  const delivery = useEmailDeliverability();
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState<{
    kind: 'info' | 'error';
    text: string;
  } | null>(null);

  const exportCsv = useCallback(async () => {
    setExportNotice(null);
    // Alerts belong to a site, and a signed-in user can legitimately have no
    // site assigned. Every other screen answers that with `NoSiteNotice`;
    // Profile cannot, because the rest of it -- theme, notifications, sign-in
    // -- is about the PERSON and works perfectly well without one.
    if (!site) {
      setExportNotice({
        kind: 'info',
        text: 'No site is assigned to your account, so there are no alerts to export.',
      });
      return;
    }
    setExporting(true);
    try {
      // Fetched, not read from the bell's cache: that cache holds
      // `?state=open` only, and an export for an audit or a review of
      // an incident that is over is exactly the closed ones.
      const rows = (await fetchAlertsForExport(site.id))
        .filter((a) => a !== null && typeof a === 'object')
        .map((a) => adaptAlert(a))
        .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
      if (rows.length === 0) {
        setExportNotice({ kind: 'info', text: 'This site has no alerts to export.' });
        return;
      }
      const lines = [CSV_HEADER, ...rows.map(alertToCsvRow)];
      const csv = lines.join('\n') + '\n';
      const safeSiteId = site.id.replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `nexsoss-alerts-${safeSiteId}-${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:.]/g, '-')}.csv`;
      downloadBlob(filename, csv, 'text/csv;charset=utf-8');
      setExportNotice({
        kind: 'info',
        text:
          `Exported ${rows.length} alert${rows.length === 1 ? '' : 's'} (open and closed) to ${filename}.` +
          // Said only when it happened: the server caps a single read,
          // so a busy site's export is a recent window rather than the
          // whole history, and an audit must not read it as the whole.
          (rows.length >= EXPORT_LIMIT
            ? ` This is the most recent ${EXPORT_LIMIT}, the most the server returns at once — older alerts are not included.`
            : ''),
      });
    } catch (err) {
      setExportNotice({
        kind: 'error',
        text: `Export failed: ${(err as Error).message}`,
      });
    } finally {
      setExporting(false);
    }
  }, [site]);

  // Every reason mail would not arrive, phrased as what to do about
  // it. They are independent and can all be true at once, so this is a
  // list rather than the first thing that happens to be wrong.
  const deliveryBlockers = useMemo(() => {
    const d = delivery.data;
    if (!d || d.deliverable) return [];
    const blockers: string[] = [];
    if (!d.provider_configured) {
      blockers.push(
        `This server cannot send email at all${d.provider_detail ? ` — ${d.provider_detail}` : ''}. An administrator has to configure a mail provider; until then nobody here is emailed about anything.`,
      );
    }
    if (!d.address_known) {
      blockers.push(
        'We have no email address for your account, so there is nowhere to send it. Sign in again once your address is set on your identity provider account.',
      );
    }
    if (d.notified_site_count === 0) {
      // Two different situations that both read as "no sites", and
      // they need opposite instructions. An operator has to go and ask
      // somebody; a platform admin — who holds no assignment rows by
      // design and cannot be given one — only has to tick a box below.
      // Telling an admin to ask an administrator for access they
      // already have is what the old copy did.
      blockers.push(
        d.all_sites_access
          ? 'You have not chosen any sites to be emailed about. You reach every site by role rather than by assignment, so alert email has nothing to follow until you pick the sites you want below.'
          : 'You are not assigned to any site. Alert email follows site assignments, so you would be emailed about nothing — ask an administrator to assign you a site.',
      );
    }
    return blockers;
  }, [delivery.data]);

  const updateCleaned = useCallback(
    (patch: Partial<typeof prefs.cleanedZones>) =>
      update({ cleanedZones: { ...prefs.cleanedZones, ...patch } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefs.cleanedZones, update],
  );
  const updateUi = useCallback(
    (patch: Partial<typeof prefs.ui>) => update({ ui: { ...prefs.ui, ...patch } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefs.ui, update],
  );

  // Server-side defaults, restated here so the controls render in a
  // known position before the first fetch resolves rather than
  // flipping from unchecked to checked under the pointer.
  const emailEnabled = emailPrefs.data?.email_enabled ?? false;
  // A stored 'info' floor shows as 'warning': the two pass exactly the
  // same alerts while nothing opens an info alert, and the select has
  // no 'info' option to match it against.
  const emailMinSeverity: AlertSeverity =
    emailPrefs.data?.min_severity === 'critical' || emailPrefs.data === undefined
      ? 'critical'
      : 'warning';
  const emailDisabled =
    emailPrefs.isPending || emailPrefs.isError || updateEmailPrefs.isPending;

  /** The explicit site selection, `null` while it is "everywhere I am
   *  assigned". Kept as null rather than flattened to `[]` because the
   *  two mean opposite things to the server. */
  const emailSites = emailPrefs.data?.sites ?? null;
  const emailSiteSet = useMemo(() => new Set(emailSites ?? []), [emailSites]);
  /** Ticking a site off the "all my sites" default has to send the
   *  remaining ones explicitly, so the first click materialises the
   *  implicit set. Doing it the other way — sending just the one site
   *  clicked — would silently unsubscribe them from the rest. */
  const toggleEmailSite = useCallback(
    (siteId: string, on: boolean) => {
      const base = emailSites ?? sites.map((s) => s.site);
      const next = on ? [...base, siteId] : base.filter((s) => s !== siteId);
      // Everything selected is the default in a different spelling.
      // Storing it as a list would freeze the set, so a site added to
      // this account later would silently not be included.
      const all = sites.length > 0 && next.length === sites.length;
      updateEmailPrefs.mutate({ sites: all ? [] : next });
    },
    [emailSites, sites, updateEmailPrefs],
  );

  return (
    <div className="nx-profile">
      <header className="nx-profile__head">
        <h1 className="nx-section-title">Profile — {user?.displayName ?? 'Operator'}</h1>
      </header>
      <HelpScreenNote screen="profile" />

      <RoleGuard allowedRoles={['nexsoss:platform_admin', 'nexsoss:site_supervisor', 'nexsoss:service_operator', 'nexsoss:foh_operator']}>
        {/* Low-battery alerts (Akash, 2026-10-31, spa 0.5.69). The site raises
            them for everyone at levels set on the server
            (`robot_battery_low`); the one choice here is whether the LOW one
            pops up on this computer. The numbers in these words are held to
            the server's by `battery-levels-contract.test.ts`. */}
        <section className="nx-profile__section" data-testid="profile-battery">
          <h2>Low-battery alerts</h2>
          <p className="nx-profile__hint" data-testid="profile-battery-hint">
            The system raises an alert when a robot with no job has a battery under{' '}
            {BATTERY_LOW_PCT}% and has not moved or charged for {BATTERY_LOW_AFTER_MIN} minutes.
            A robot on a job is left to finish it. The alert becomes urgent when the battery is
            under {BATTERY_VERY_LOW_PCT}%, job or not. Everyone at the hotel sees these in the
            bell.
            {/* Only to a role that has the Alerts screen: the front desk's
                `/alerts` goes to the Overview (reviews V4-2, I1-F2). */}
            {canSeeAlerts && ' You also see them on the Alerts screen.'} The tick box below only
            decides what pops up on this computer.
          </p>
          <fieldset className="nx-profile__radio">
            <label>
              <input
                type="checkbox"
                checked={prefs.battery.lowPopups}
                onChange={(e) => update({ battery: { lowPopups: e.target.checked } })}
                data-testid="profile-battery-low-popups"
              />
              <span>
                <strong>Pop up for low battery (under {BATTERY_LOW_PCT}%)</strong>
                <em>
                  On by default. Very low battery (under {BATTERY_VERY_LOW_PCT}%) always pops
                  up.
                </em>
              </span>
            </label>
            <HelpNote id="profile.battery.popup" />
          </fieldset>
          <HelpNote id="profile.battery.levels" />
        </section>

        <section className="nx-profile__section">
          <h2>Fire alarm audio</h2>
          <p className="nx-profile__hint">
            The fire alarm always shows a visual overlay. Audio follows the mode you pick below.
          </p>
          <fieldset className="nx-profile__radio">
            <label>
              <input
                type="radio"
                name="fire-audio"
                value="always"
                checked={prefs.fireAlarmAudio === 'always'}
                onChange={() => update({ fireAlarmAudio: 'always' })}
                data-testid="profile-fire-audio-always"
              />
              <span>
                <strong>Always play</strong>
                <em>The siren plays even when master mute is on. Use this in 24/7 sites.</em>
              </span>
            </label>
            <label>
              <input
                type="radio"
                name="fire-audio"
                value="mute-with-rest"
                checked={prefs.fireAlarmAudio === 'mute-with-rest'}
                onChange={() => update({ fireAlarmAudio: 'mute-with-rest' as FireAlarmMode })}
                data-testid="profile-fire-audio-mute-with-rest"
              />
              <span>
                <strong>Mute with the rest</strong>
                <em>Default. The siren respects the master mute toggle in the top bar.</em>
              </span>
            </label>
            <label>
              <input
                type="radio"
                name="fire-audio"
                value="never"
                checked={prefs.fireAlarmAudio === 'never'}
                onChange={() => update({ fireAlarmAudio: 'never' })}
                data-testid="profile-fire-audio-never"
              />
              <span>
                <strong>Never play</strong>
                <em>Visual only. Use only when a physical klaxon is wired to the site.</em>
              </span>
            </label>
          </fieldset>
        </section>

        <section className="nx-profile__section">
          <h2>Alert menu</h2>
          <div className="nx-profile__grid">
            <label className="nx-profile__row">
              <span>Bell dropdown height (px)</span>
              <input
                type="number"
                min={120}
                max={800}
                step={20}
                value={prefs.ui.bellMaxHeightPx}
                onChange={(e) =>
                  updateUi({ bellMaxHeightPx: Math.max(120, Math.min(800, Number(e.target.value))) })
                }
                data-testid="profile-bell-height"
              />
            </label>
            <label className="nx-profile__row">
              <span>Auto-archive after (minutes)</span>
              <input
                type="number"
                min={5}
                max={1440}
                step={5}
                value={prefs.ui.autoArchiveAfterMinutes}
                onChange={(e) =>
                  updateUi({ autoArchiveAfterMinutes: Math.max(5, Math.min(1440, Number(e.target.value))) })
                }
                data-testid="profile-auto-archive"
              />
            </label>
          </div>
          <p className="nx-profile__hint">
            Read alerts are automatically archived after this window so the bell menu stays focused on what's recent.
          </p>
        </section>

        {/* Cleaned-zone colouring (Akash, 2026-10-22). The ONLY place it is
            set: the views carry a legend and no control. Per person, per
            browser, like everything above. */}
        <section className="nx-profile__section" data-testid="profile-cleaned">
          <h2>Cleaned-zone colours</h2>
          <p className="nx-profile__hint">
            Colours each cleaning zone by when a robot last finished cleaning it: green when
            just cleaned, fading to grey. Grey means not cleaned within the days below, or
            never. Any finished clean counts, whatever its type; a failed or cancelled one
            does not.
          </p>
          <fieldset className="nx-profile__radio">
            <label>
              <input
                type="checkbox"
                checked={prefs.cleanedZones.building}
                onChange={(e) => updateCleaned({ building: e.target.checked })}
                data-testid="profile-cleaned-building"
              />
              <span>
                <strong>Show on the Building view</strong>
                <em>The 3D building on the Overview. On by default.</em>
              </span>
            </label>
            <HelpNote id="profile.cleaned.building" />
            <label>
              <input
                type="checkbox"
                checked={prefs.cleanedZones.map}
                onChange={(e) => updateCleaned({ map: e.target.checked })}
                data-testid="profile-cleaned-map"
              />
              <span>
                <strong>Show on the 2D map</strong>
                <em>The flat floor map on the Overview. Off by default.</em>
              </span>
            </label>
            <HelpNote id="profile.cleaned.map" />
          </fieldset>
          <div className="nx-profile__grid">
            <label className="nx-profile__row">
              <span>Fully grey after</span>
              {/* A list, not a number box: every allowed value is on it, so
                  there is no half-typed or out-of-range state to repair. */}
              <select
                value={prefs.cleanedZones.days}
                onChange={(e) => updateCleaned({ days: clampCleanedDays(e.target.value) })}
                data-testid="profile-cleaned-days"
              >
                {CLEANED_DAY_CHOICES.map((d) => (
                  <option key={d} value={d}>
                    {d === 1 ? '1 day' : `${d} days`}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <HelpNote id="profile.cleaned.days" />
        </section>

        <section className="nx-profile__section">
          <h2>Email notifications</h2>
          <p className="nx-profile__hint">
            Sent by the server rather than by this console, so they do not need a browser to be open — but only for the sites you are assigned to, and only while the server has a working mail provider. Anything standing in the way of that is listed below. The bell and toasts here are unaffected either way, and they remain the only channel that is certain to work.
          </p>
          {deliveryBlockers.length > 0 && (
            <AlertBanner
              variant="warning"
              title={
                emailEnabled
                  ? 'These emails are not reaching you'
                  : 'Turning this on would not page you yet'
              }
            >
              <ul>
                {deliveryBlockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            </AlertBanner>
          )}
          {emailPrefs.isError && (
            <AlertBanner variant="error" title="Preferences unavailable">
              Could not load your email settings. The controls below are disabled until they load.
            </AlertBanner>
          )}
          {updateEmailPrefs.isError && (
            <AlertBanner variant="error" title="Not saved">
              {(updateEmailPrefs.error as Error).message}
            </AlertBanner>
          )}
          <fieldset className="nx-profile__radio" disabled={emailDisabled}>
            <label>
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(e) => updateEmailPrefs.mutate({ email_enabled: e.target.checked })}
                data-testid="profile-email-enabled"
              />
              <span>
                <strong>Email me when an alert opens</strong>
                <em>Off unless you turn it on. The bell and toasts are unaffected either way.</em>
              </span>
            </label>
          </fieldset>
          <div className="nx-profile__grid">
            <label className="nx-profile__row">
              <span>Minimum severity to email</span>
              <select
                value={emailMinSeverity}
                disabled={emailDisabled}
                onChange={(e) =>
                  updateEmailPrefs.mutate({ min_severity: e.target.value as AlertSeverity })
                }
                data-testid="profile-email-min-severity"
              >
                {/* Only the two the evaluator actually opens today
                    (`alerts_engine.py`): critical for robot errors and
                    lift faults, warning for the rest. An "info" option
                    would read as a subscription to something that
                    never arrives. */}
                <option value="warning">Warning and above</option>
                <option value="critical">Critical only</option>
              </select>
            </label>
          </div>
          {/* Only worth showing when there is a choice to make. One
              site and no all-sites access means the answer is already
              decided, and a checkbox that can only be wrong is noise. */}
          {(sites.length > 1 || delivery.data?.all_sites_access) && (
            <div className="nx-profile__sites" data-testid="profile-email-sites">
              <span className="nx-profile__sites-label">Sites to email me about</span>
              <p className="nx-profile__hint">
                {emailSites === null
                  ? delivery.data?.all_sites_access
                    ? 'Nothing chosen. You reach every site by role rather than by assignment, so no site is following you by default — tick the ones you want.'
                    : 'Every site you are assigned to. Gaining a site starts its email and losing it stops it, with nothing to change here.'
                  : 'Only the sites ticked below. Sites you are assigned to but have not ticked will not email you.'}
              </p>
              <fieldset className="nx-profile__sites-list" disabled={emailDisabled}>
                {sites.map((s) => {
                  // An unmade selection means "all of them" for
                  // somebody with assignments, and "none of them" for
                  // an admin who has none to follow. Rendering both as
                  // ticked would tell an admin they are subscribed to
                  // sites that will never mail them.
                  const checked =
                    emailSites === null
                      ? !delivery.data?.all_sites_access
                      : emailSiteSet.has(s.site);
                  return (
                    <label key={s.site}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggleEmailSite(s.site, e.target.checked)}
                        data-testid={`profile-email-site-${s.site}`}
                      />
                      <span>{s.label || s.site}</span>
                    </label>
                  );
                })}
              </fieldset>
            </div>
          )}
        </section>

        <section className="nx-profile__section">
          <h2>Export alerts</h2>
          <p className="nx-profile__hint">
            Download {site ? `${site.label}'s` : 'this site\u2019s'} alerts as a
            CSV — open and closed, most recent first, up to {EXPORT_LIMIT} rows,
            with each timestamp in both ISO-8601 UTC and its raw form. Suitable
            for compliance audits and post-incident reviews.
          </p>
          <button
            type="button"
            className="nx-profile__btn"
            onClick={exportCsv}
            disabled={exporting}
            data-testid="profile-export-csv"
          >
            {exporting ? 'Exporting…' : 'Export alerts (CSV)'}
          </button>
          {exportNotice && (
            <AlertBanner variant={exportNotice.kind === 'error' ? 'error' : 'info'} title={exportNotice.kind === 'error' ? 'Export failed' : 'Export ready'}>
              {exportNotice.text}
            </AlertBanner>
          )}
        </section>

        <section className="nx-profile__section">
          <h2>Reset</h2>
          <button
            type="button"
            className="nx-profile__btn nx-profile__btn--danger"
            onClick={reset}
            data-testid="profile-reset"
          >
            Reset preferences to defaults
          </button>
        </section>
      </RoleGuard>
    </div>
  );
}